/**
 * @module LegendSetting/seriesSortUtils
 * @description 图例系列排序 — 从 chartData 提取系列名（顺序与计算接口一致）
 */

import { ChartType, type TChartType } from '@dbl/core/chart';

/** 用于 useMemo 依赖：chartData 引用不变时仍能感知 series 变化 */
export function pickSeriesSourceForQuickDrag(
  chartType: TChartType,
  chartData: Record<string, unknown> | undefined,
): unknown {
  if (!chartData) return null;

  if (chartType === ChartType.HorizontalBarChart) {
    return (
      chartData.horizontalBarData as Array<Record<string, unknown>> | undefined
    )?.[0]?.series;
  }

  if (chartType === ChartType.PercentStackBar) {
    return (
      chartData.percentStackBarData as Record<string, unknown> | undefined
    )?.seriesData;
  }

  if (chartType === ChartType.PercentLine) {
    return (
      chartData.percentLineChartData as Record<string, unknown> | undefined
    )?.seriesData;
  }

  if (chartType === ChartType.StackBar) {
    return (
      chartData.stackBarChart as Record<string, unknown> | undefined
    )?.seriesData;
  }

  if (chartType === ChartType.Bar) {
    return (chartData.barChart as Record<string, unknown> | undefined)?.seriesData;
  }

  const { barChart, horizontalBarData, percentStackBarData, stackBarChart } =
    chartData;

  return (
    (barChart as Record<string, unknown> | undefined)?.seriesData
    || ((horizontalBarData as Array<Record<string, unknown>>)?.[0]?.series)
    || (percentStackBarData as Record<string, unknown> | undefined)?.seriesData
    || (stackBarChart as Record<string, unknown> | undefined)?.seriesData
    || null
  );
}

/** 从 seriesData 数组按接口顺序提取系列名（有序去重） */
export function extractSeriesNamesFromSource(seriesData: unknown): string[] {
  if (!Array.isArray(seriesData)) return [];

  const names: string[] = [];
  const seen = new Set<string>();
  seriesData.forEach((item: unknown) => {
    const name = (item as Record<string, unknown>)?.name;
    if (!name) return;
    const text = String(name);
    if (seen.has(text)) return;
    seen.add(text);
    names.push(text);
  });

  return names;
}

/**
 * 按图表类型从 chartData 提取系列名称列表
 */
export function extractSeriesQuickDragList(
  chartType: TChartType,
  chartData: Record<string, unknown> | undefined,
): string[] {
  if (!chartData) return [];
  try {
    const source = pickSeriesSourceForQuickDrag(chartType, chartData);
    return extractSeriesNamesFromSource(source);
  } catch (error) {
    console.log('[ChartSettingsV2] extractSeriesQuickDragList failed', {
      chartType,
      error,
    });
    return [];
  }
}

/**
 * @deprecated 请使用 extractSeriesQuickDragList(chartType, chartData)
 * 保留兼容：无 chartType 时按字段兜底链提取
 */
export function extractSeriesNames(
  chartData: Record<string, unknown> | undefined,
): string[] {
  return extractSeriesQuickDragList(ChartType.Bar, chartData);
}

